import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Network, Server, ArrowRight, CheckCircle2, Users, Globe, Award, Clock, TrendingUp, Lock, Zap, Brain, Workflow, Database, Cpu, Cloud } from 'lucide-react';

const Home = () => {
  // Scroll reveal animation
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);
  const services = [
    {
      icon: Shield,
      title: 'Cyber Security',
      description: 'SIEM, SOAR & Managed SOC - Advanced cybersecurity solutions with proactive monitoring, automated threat response, and 24/7 SOC operations tailored to your needs.',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop',
      link: '/services/cybersecurity',
      features: ['24/7 Threat Monitoring', 'AI-Driven Analytics', 'Managed SOC', 'Compliance Ready']
    },
    {
      icon: Server,
      title: 'System Integration',
      description: 'Turnkey ICT Projects & Smart Infrastructure - From greenfield IT infrastructure to structured cabling, IoT, surveillance, access control, and FTTH deployments.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop',
      link: '/services/system-integration',
      features: ['Data Center Solutions', 'Cloud Integration', 'Network Security', 'Smart Infrastructure']
    },
    {
      icon: Network,
      title: 'ISP Solutions',
      description: 'High-Performance Connectivity - Last-mile delivery for major telcos and enterprise-grade ILL, P2P, SD-WAN, and ultra-low latency connectivity for mission-critical operations.',
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&h=400&fit=crop',
      link: '/services/network-services',
      features: ['99.95% Uptime SLA', 'SD-WAN Solutions', 'Managed Wi-Fi', 'Multi-Cloud Connectivity']
    },
    {
      icon: Brain,
      title: 'AI & Intelligent Systems',
      description: 'Enterprise GenAI, Autonomous Agents & AI Copilots - Private, secure AI platforms with GPT, Claude, Gemini deployments operating on your data within your infrastructure.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop',
      link: '/services/ai-intelligent-systems',
      features: ['Enterprise GenAI', 'AI Copilots', 'Autonomous Agents', 'Private AI']
    },
    {
      icon: Workflow,
      title: 'Business Automation & RPA',
      description: 'AI-Driven Process Automation - Finance, HR, IT, Sales, Compliance automation. Digital workers that operate 24/7 with 100% accuracy.',
      image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=400&fit=crop',
      link: '/services/automation-rpa',
      features: ['Finance Automation', 'HR Automation', 'IT Operations', 'Compliance Automation']
    },
    {
      icon: Database,
      title: 'Data Platforms & Intelligence',
      description: 'Big Data, Real-Time Analytics & BI - Convert raw data into live dashboards, executive insights and predictive business intelligence.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
      link: '/services/data-platforms',
      features: ['Big Data Platforms', 'Real-Time Analytics', 'BI Dashboards', 'Predictive Analytics']
    },
    {
      icon: Cpu,
      title: 'IoT & Smart Infrastructure',
      description: 'IoT, Edge AI & Smart Systems - Sensors, edge computing and AI combine to create intelligent, automated physical environments.',
      image: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=600&h=400&fit=crop',
      link: '/services/iot-smart-infra',
      features: ['Smart Buildings', 'Industrial IoT', 'Video Analytics', 'Energy Monitoring']
    },
    {
      icon: Cloud,
      title: 'Cloud & DevOps',
      description: 'AWS, Azure, Kubernetes & MLOps - Scalable, secure, AI-ready cloud and digital platforms with automated CI/CD pipelines.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
      link: '/services/cloud-devops',
      features: ['AWS & Azure', 'Kubernetes', 'CI/CD', 'MLOps']
    }
  ];

  const stats = [
    { icon: Award, number: '50+', label: 'Industry Awards', description: 'Recognized for excellence' },
    { icon: CheckCircle2, number: '100+', label: 'Completed Projects', description: 'Successful implementations' },
    { icon: Globe, number: '200+', label: 'Points of Presence', description: 'Pan-India coverage' },
    { icon: Users, number: '5,000+', label: 'Happy Customers', description: 'Trusted by enterprises' }
  ];

  const certifications = [
    { name: 'ISO 27001', desc: 'Information Security' },
    { name: 'ISO 9001', desc: 'Quality Management' },
    { name: 'ISO 20000', desc: 'IT Service Management' },
    { name: 'ISO 22301', desc: 'Business Continuity' },
    { name: 'CMM Level 5', desc: 'Process Maturity' },
    { name: 'SOC 2', desc: 'Security & Privacy' },
    { name: 'Class A ISP', desc: 'Licensed Service Provider' }
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: '20+ Years of Excellence',
      description: 'Two decades of pioneering ICT solutions across diverse industries'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Certified professionals with deep domain expertise in technology'
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Round-the-clock monitoring and rapid incident response'
    },
    {
      icon: TrendingUp,
      title: 'Proven Track Record',
      description: '5000+ satisfied customers across banking, education, and enterprises'
    },
    {
      icon: Lock,
      title: 'Security First',
      description: 'ISO 27001 certified with advanced cybersecurity measures'
    },
    {
      icon: Zap,
      title: 'Innovation Driven',
      description: 'Cutting-edge solutions in AI, ML, IoT, and Blockchain'
    }
  ];

  const industries = [
    { name: 'Banking & Finance', icon: '🏦', image: 'https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?w=400&h=300&fit=crop' },
    { name: 'Healthcare', icon: '🏥', image: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=400&h=300&fit=crop' },
    { name: 'Education', icon: '🎓', image: 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=400&h=300&fit=crop' },
    { name: 'Government', icon: '🏛️', image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=400&h=300&fit=crop' },
    { name: 'Manufacturing', icon: '🏭', image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=400&h=300&fit=crop' },
    { name: 'Retail', icon: '🏬', image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=400&h=300&fit=crop' }
  ];

  const blogPosts = [
    {
      title: 'The Importance of Cybersecurity in Today\'s Digital World',
      excerpt: 'In today\'s digital world, cybersecurity is more important than ever. With the increasing use of technology and the growth of cyber threats...',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=600&fit=crop',
      category: 'Cybersecurity',
      date: 'March 15, 2025',
      slug: 'cybersecurity-importance'
    },
    {
      title: 'System Integration and IT Infrastructure: Enhancing Business Operations',
      excerpt: 'In today\'s fast-paced business world, companies rely heavily on IT infrastructure to support and streamline their operations...',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=600&fit=crop',
      category: 'System Integration',
      date: 'March 12, 2025',
      slug: 'system-integration'
    },
    {
      title: 'Unlocking the Power of Artificial Intelligence and Machine Learning',
      excerpt: 'Artificial Intelligence (AI) and Machine Learning (ML) are two of the most exciting and rapidly developing fields of technology...',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop',
      category: 'Technology',
      date: 'March 10, 2025',
      slug: 'ai-ml-power'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white py-16 md:py-32 overflow-hidden animate-gradient">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="animate-slideInLeft">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
                Empowering Businesses with <span className="text-blue-300">Innovative</span> and <span className="text-blue-300">Secure</span> Solutions
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-blue-100">
                At Sanghvi Infotech Pvt. Ltd., our mission is to deliver the best IT solutions that drive efficiency and growth for your business with over 20 years of proven expertise.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Link to="/about" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-blue-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-8 py-5 md:py-6 transition-all hover:scale-105">
                    Know More
                    <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                  </Button>
                </Link>
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-blue-900 text-base md:text-lg px-6 md:px-8 py-5 md:py-6 transition-all hover:scale-105">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block animate-slideInRight">
              <img 
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=700&fit=crop" 
                alt="Technology Infrastructure" 
                className="rounded-lg shadow-2xl animate-float"
              />
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Certifications Banner */}
      <section className="py-8 md:py-12 bg-white border-b">
        <div className="container mx-auto px-4 md:px-6">
          <h3 className="text-center text-sm sm:text-base md:text-lg text-gray-700 font-semibold mb-4 md:mb-6 animate-fadeIn">Trusted & Certified by Leading Standards</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 md:gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="text-center group cursor-pointer animate-scaleIn" style={{animationDelay: `${index * 50}ms`}}>
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg shadow-sm group-hover:shadow-md transition-all hover:scale-105">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600 mb-1">{cert.name}</div>
                  <div className="text-[10px] sm:text-xs text-gray-500">{cert.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16 scroll-reveal">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Empowering the Digital Backbone of Modern Enterprises
            </h2>
            <div className="w-20 md:w-24 h-1 bg-blue-600 mx-auto mb-4 md:mb-6"></div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              At Sanghvi Infotech, we deliver cutting-edge solutions in Cybersecurity, System Integration, and ISP services. From securing networks to building intelligent infrastructure and providing ultra-reliable connectivity — we power your growth with technology that performs.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="scroll-reveal border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-3 group overflow-hidden card-hover" style={{animationDelay: `${index * 150}ms`}}>
                  <div className="relative h-40 sm:h-48 overflow-hidden image-hover">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <IconComponent className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl sm:text-2xl mb-2">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm sm:text-base leading-relaxed">{service.description}</CardDescription>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to={service.link}>
                      <Button variant="link" className="px-0 text-blue-600 hover:text-blue-800 font-semibold transition-all hover:translate-x-1">
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Why Choose Sanghvi Infotech?</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine technical excellence with unparalleled customer service to deliver solutions that drive real business value
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="group">
                  <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 h-full border border-gray-100 hover:border-blue-200">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                      <IconComponent className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <h2 className="text-5xl font-bold text-center mb-16">Our Accomplishments</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-4 group-hover:bg-white/20 transition-colors">
                    <IconComponent className="h-10 w-10" />
                  </div>
                  <div className="text-6xl font-bold mb-2">{stat.number}</div>
                  <div className="text-2xl font-semibold text-blue-100 mb-1">{stat.label}</div>
                  <div className="text-sm text-blue-200">{stat.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Industries We Serve</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by leading organizations across diverse sectors for mission-critical ICT solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="relative h-64">
                  <img src={industry.image} alt={industry.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="text-4xl mb-2">{industry.icon}</div>
                    <h3 className="text-2xl font-bold text-white">{industry.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Latest Insights</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">Stay updated with the latest trends in technology and cybersecurity</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-none shadow-lg group">
                <div className="relative h-56 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">{post.category}</span>
                  </div>
                </div>
                <CardHeader>
                  <div className="text-sm text-gray-500 mb-2">{post.date}</div>
                  <CardTitle className="text-xl mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">{post.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4 line-clamp-3">{post.excerpt}</CardDescription>
                  <Link to={`/blog/${post.slug}`}>
                    <Button variant="link" className="px-0 text-blue-600 font-semibold">
                      Read More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/blog">
              <Button size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8">
                View All Posts
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 to-blue-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-10 text-blue-100 max-w-3xl mx-auto">
            Let's discuss how our innovative solutions can help your organization achieve its digital transformation goals. Our experts are ready to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-10 py-6">
                Contact Us Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-10 py-6">
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;